import PromisePool from "es6-promise-pool";

export function promiseDelegate<T>(): [Promise<T>, (v: T)=>void, (r: any)=>void] {
    let done = false;
    let error: any = undefined;
    let result: T|undefined = undefined;

    let r: (v: T) => void = (v) => { done = true; result = v; };
    let j: (e: any) => void = (e) => { done = true; error = e; };

    const resultPromise = new Promise<T>((_r, _j) => {
        if (done) {
            if (error !== undefined) _j(error);
            else                     _r(result!);

            error = undefined;
            result = undefined;

            return;
        } else {
            r = _r;
            j = _j;
        }
    });

    return [
        resultPromise,
        (v) => r(v),
        (e) => j(e)
    ]
}

type _CancellableWaiter<P, T> = {type: "ok"|"skip", r: (v: P) => T, j: (err: any) => void};

function abortError(reason: string) {
    const ac = new AbortController();
    ac.abort(reason);
    try {
        ac.signal.throwIfAborted();
    } catch (e) {
        return e;
    }
    return new Error(reason);
}
function makeSkip<P, T>(item: _CancellableWaiter<P, T>, c: (v: any) => void, signal?: AbortSignal) {
    return () => {
        item.type = "skip";
        item.r = noop as any;
        item.j = noop as any;
        c(abortError(signal?.reason || "Aborted"));
        c = () => {};
    };
}
function noop() {}
type ArrayElement<ArrayType extends readonly unknown[]> = 
  ArrayType extends readonly (infer ElementType)[] ? ElementType : never;
export class Channel<T> {
    private _readers: _CancellableWaiter<T, void>[] = [];
    private _writers: _CancellableWaiter<void, T>[] = [];
    private _closed: boolean = false;

    get closed(): boolean {
        return this._closed;
    }

    close() {
        if (this._closed === false) {
            const readers = this._readers;
            this._readers = [];

            const writers = this._writers;
            this._writers = [];

            const closed = abortError("Channel closed");
            readers.forEach(w => w.type == "ok" && w.j(closed));
            writers.forEach(w => w.type == "ok" && w.j(closed));
        }
        this._closed = true;
    }

    private static async _exchange<R, V>(
            receivers: _CancellableWaiter<V, R>[],
            senders: _CancellableWaiter<R, V>[],
            value: V,
            abort?: AbortSignal
    ): Promise<R> {
        abort?.throwIfAborted();

        while (receivers.length > 0) {
            let next = receivers.shift()!;
            if (next.type == "skip") continue;
            return next.r(value);
        }

        let [result, r, j] = promiseDelegate<R>();[]
        const _current: ArrayElement<typeof senders> = {
            type: "ok",
            r: ((_r) => (v: R) => {_r(v); _r = null as any; return value;})(r),
            j
        }
        senders.push(_current);
        let ms = makeSkip<R, V>(_current, j, abort);
        abort?.addEventListener("abort", ms, {once: true});
        r = null as any;
        j = null as any;

        try {
            return await result;
        } finally {
            ms();
            abort?.removeEventListener("abort", ms);
            ms = noop;
        }
    }

    async receive(abort?: AbortSignal): Promise<T> {
        if (this._closed)
            throw new Error("Channel closed");

        return await Channel._exchange(
            this._writers,
            this._readers,
            void 0,
            abort
        )
    }

    async send(value: T, abort?: AbortSignal): Promise<void> {
        if (this._closed) {
            throw new Error("Channel closed");
        }

        return await Channel._exchange(
            this._readers,
            this._writers,
            value,
            abort
        )
    }
}


export async function* callback2generator<V>(cb: (send: (value: V) => Promise<void>) => Promise<void>): AsyncGenerator<V> {
    const channel = new Channel<{state: "finish", value: null}|{state: "error", value: any}|{state: "yield", value: V}>;
    const p = (async () => {
        try {
            await cb(async (value) => {
                await channel.send({state: "yield", value});
            });
        } catch (e) {
            await channel.send({state: "error", value: e});
        }
        await channel.send({state: "finish", value: null});
    })();

    while (true) {
        const {state, value} = await channel.receive();
        if (state === "finish") {
            break;
        } else if (state === "error") {
            throw value;
        } else {
            yield value;
        }
    }

    await p;
}

export class Queue {
    private queue: Promise<void> = Promise.resolve();

    enqueue<T>(cb: () => Promise<T>, abort?: AbortSignal): Promise<T> {
       const currentQueue = this.queue;
       const [resultPromise, r, j] = promiseDelegate<T>();

       const onAbort = () => { cb = async () => {abort?.throwIfAborted(); return void 0 as never; }; };
       abort?.addEventListener("abort", onAbort, {once: true});

       this.queue = new Promise<void>(rs => {
            function finish() {
                abort?.removeEventListener("abort", onAbort);
                rs();
            }
            currentQueue.then(() => {
                const p = cb();
                p.then(() => finish(), () => finish());
                p.then(r, j);
            });
       });

       return resultPromise;
    }
}


export class MultiQueue {
    private _pool: PromisePool<void>;
    private _channel: Channel<() => Promise<void>> = new Channel();

    constructor(slots: number) {
        this._pool = new PromisePool(() => {
            if (this._channel.closed)
                return;

            return (async () => {
                let next: () => Promise<void>;
                try {
                    next = await this._channel.receive();
                } catch (e) {return;}
                return await next();
            })();
        }, slots);
        this._pool.start().then(() => {}, window.reportError);
    }

    async enqueue<T>(cb: () => Promise<T>): Promise<T> {
        const [resultPromise, r, j] = promiseDelegate<T>();

        this._channel.send((async () => {
            const waiter = cb()
            waiter.then(r, j);
            await waiter;
        }));

        return await resultPromise;
    }

    close() { this._channel.close(); }

}


export class Reloader<T> {
    static EMPTY_PROMISE =
        (() => {
            const p = Promise.reject(new Error("Invalid state"));
            p.catch(() => {});
            return p;
        })();
    private cb: () => Promise<T>;
    private _running: boolean = false;
    private _rerun: ((v: Promise<T>) => void)[] = [];
    private _promise: Promise<T> = Reloader.EMPTY_PROMISE as Promise<T>;

    constructor(cb: () => Promise<T>) {
        this.cb = cb;
    }

    reload() {
        if (this._running) {
            const [p, rs, rj] = promiseDelegate<T>();
            this._rerun.push((new_promise) => {
                new_promise.then(rs, rj);
            });;
            return p;
        };
        const cbs = this._rerun;
        this._rerun = [];
        this._running = true;
        this._promise = this.cb();
        cbs.forEach(cb => cb(this._promise));

        const _finally = () => {
            this._running = false;
            if (this._rerun.length > 0) this.reload();
        };
        this._promise.then(_finally, _finally);
        return this._promise;
    }
}


export class SlowSignal<T> {
    private current: Promise<T>;
    private resolve: (v: T) => void;
    private resolved: boolean = false;

    constructor() {
        let reject;
        ([this.current, this.resolve, reject] = promiseDelegate());
    }

    set(v: T) {
        if (this.resolved) {
            this.unset();
        }
        this.resolve(v);
        this.resolved = true;
    }

    unset() {
        let reject;
        ([this.current, this.resolve, reject] = promiseDelegate());
    }

    get promise(): Promise<T> {
        return this.current;
    }
}


export class Joiner<T> {
    private _func: () => Promise<T>;
    private _current: {running: false}|{running: true, value: Promise<T>} = {running: false};

    constructor(func: () => Promise<T>) {
        this._func = func;
    }

    fetch(): Promise<T> {
        if (!this._current.running) {
            const op = this._func();
            const reset = () => { this._current = {running: false} };
            op.then(reset, reset);

            this._current = {running: true, value: op};
        }

        return (this._current as any).value;
    }
}


export function event(): [(cb: () => void) => void, () => void] {
    const [p, rs, _] = promiseDelegate<void>();
    return [
        (c) => p.then(() => c()),
        () => rs()
    ]
}


export type Timeout = ReturnType<typeof window.setTimeout> | ReturnType<typeof setTimeout>;


export async function* liveReorder<T, N = number>(
  src: AsyncIterator<T>,
  key: (v: T) => N,
  starting_number: N = 0 as unknown as N,
  succ: (p: N) => N = (n => ((n as unknown as number) + 1) as unknown as N)
): AsyncGenerator<T, void, unknown> {
  // Buffer by key until we can emit the next expected key.
  const pending = new Map<N, T>();
  let expected: N = starting_number;

  // Helper to flush any contiguous items already available.
  function* flushContiguous(): Generator<T, void, unknown> {
    while (pending.has(expected)) {
      const v = pending.get(expected)!;
      pending.delete(expected);
      // Advance first, then yield v
      expected = succ(expected);
      yield v;
    }
  }

  while (true) {
    // First, emit anything already ready.
    for (const v of flushContiguous()) {
      yield v;
    }

    // Pull next item from source.
    const { value, done } = await src.next();
    if (done) {
      // Source ended: final flush; if holes remain, that's a protocol violation.
      for (const v of flushContiguous()) {
        yield v;
      }
      if (pending.size !== 0) {
        throw new Error(
          `Source ended before expected key ${String(expected)} was received.`
        );
      }
      return;
    }

    const k = key(value);
    // If this is the exact next expected, emit immediately (and cascade).
    if (k === expected) {
      yield value;
      expected = succ(expected);
      continue;
    }

    // Otherwise, stash it. Detect duplicates eagerly.
    if (pending.has(k)) {
      throw new Error(`Duplicate key encountered: ${String(k)}`);
    }
    pending.set(k, value);
  }
}


export class LatencyHider<F, T> {
  /** The user-supplied handler. */
  private readonly handler: (target: F, abort: AbortSignal) => Promise<T>;

  /** Abort controller for the currently running handler (if any). */
  private currentController: AbortController | null = null;

  /** Promise for the currently running handler (if any). */
  private currentPromise: Promise<T> | null = null;

  /** Has `upload` ever been called? */
  private hasSeenUpload = false;

  constructor(handler: (target: F, abort: AbortSignal) => Promise<T>) {
    this.handler = handler;
  }

  /**
   * Start (or restart) handling the given target.
   * If a previous run is in flight, it will be aborted first.
   * Returns the promise for the *current* run.
   */
  upload(target: F): Promise<T> {
    this.hasSeenUpload = true;

    // Abort any in-flight run.
    if (this.currentController) {
      this.currentController.abort();
      // Do not record anything about the aborted run.
    }

    // Start a fresh run.
    const controller = new AbortController();
    this.currentController = controller;

    const p = this.handler(target, controller.signal);

    // Ensure we always point `currentPromise` at the latest run.
    this.currentPromise = p;

    // Cleanup when it settles — but only if it's still the current run.
    // Also, attach a catch to avoid unhandled rejections if callers ignore it.
    p.then(
      () => {
        if (this.currentPromise === p) {
          // Completed successfully; no special state to carry over per spec.
          this.currentController = null;
        }
      },
      () => {
        if (this.currentPromise === p) {
          // Failed/aborted; clear controller reference. We don't "record the abortion".
          this.currentController = null;
        }
      }
    ).catch(() => { /* swallow to prevent unhandled rejection if caller doesn't await */ });

    return p;
  }

  /**
   * True if `upload` has ever been called.
   */
  get seen_upload(): boolean {
    return this.hasSeenUpload;
  }

  /**
   * The promise for the *currently running* handler.
   * Throws immediately if `upload` was never called before.
   */
  get value(): Promise<T> {
    if (!this.currentPromise) {
      throw new Error("ContinuousHandler.value accessed before any upload()");
    }
    return this.currentPromise;
  }
}

